import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, ArrowLeft, ClipboardPaste, Loader2, Eye, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { isZoomAtDefault } from '@/utils/zoom';
import { ElevationLoadingIndicator } from '../shared/ElevationViewer';
import { MarkerActionsPopup } from '../shared/MarkerActionsPopup';

export const SFElevationsSection = ({
  // Data
  storefrontElevations,
  storefrontTypes,
  storefrontPanelList,
  selectedStorefrontElevation,
  selectedStorefrontElevationIds,
  selectedElevation,
  // Bulk marker state
  sfBulkMarkerMode,
  sfBulkMarkerSchedule,
  pendingSfBulkMarkers,
  savingSfBulkMarkers,
  // Zoom/pan state
  elevationZoom,
  elevationPan,
  isPanning,
  activeMarkerPopup,
  // Loading states
  uploadingElevation,
  bulkDeletingElevations,
  deletingSingleElevation,
  uploadProgress,
  uploadProgressPercent,
  elevationUploading,
  // Permissions
  canManageProject,
  // Navigation
  projectId,
  activeCsiTab,
  activeSubTab,
  navigate,
  // Handlers - Selection
  selectAllElevations,
  toggleElevationSelection,
  setSelectedStorefrontElevation,
  setSelectedElevation,
  // Handlers - Bulk operations
  confirmBulkDelete,
  confirmDeleteElevation,
  // Handlers - Upload
  handleElevationUpload,
  handleElevationPasteFromClipboard,
  // Handlers - Click
  handleElevationClick,
  handleImageClick,
  handleMarkerClick,
  // Handlers - Bulk markers
  setSfBulkMarkerMode,
  setSfBulkMarkerSchedule,
  handleSfBulkMarkerClick,
  handleSaveSfBulkMarkers,
  handleCancelSfBulkMarkers,
  handleRemovePendingSfMarker,
  // Handlers - Zoom/pan
  handleZoomIn,
  handleZoomOut,
  handleResetZoom,
  handlePanStart,
  handlePanMove,
  handlePanEnd,
  // Handlers - Markers
  handleMarkerMouseDown,
  handleMarkerMouseMove,
  handleMarkerMouseUp,
  handleDeleteMarker,
  setActiveMarkerPopup,
  setMarkerContext,
  // Utils
  getElevationImageUrl,
}) => {
  return (
    <TabsContent value="sf-elevations">
      {/* Loading indicator */}
      {(uploadingElevation || bulkDeletingElevations || deletingSingleElevation) && (
        <ElevationLoadingIndicator 
          message={uploadProgress || 'Processing...'}
          progressPercent={uploadProgressPercent}
        />
      )}
      
      {/* Header with bulk actions when not viewing elevation detail */}
      {!selectedStorefrontElevation && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Elevations</h2>
          <div className="flex gap-2">
            {canManageProject && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => selectAllElevations('storefront')}
                  disabled={storefrontElevations.length === 0 || uploadingElevation || bulkDeletingElevations}
                >
                  {selectedStorefrontElevationIds.length === storefrontElevations.length && storefrontElevations.length > 0 ? 'Deselect All' : 'Select All'}
                </Button>
                <Button 
                  onClick={() => confirmBulkDelete('storefront')}
                  disabled={selectedStorefrontElevationIds.length === 0 || uploadingElevation || bulkDeletingElevations}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedStorefrontElevationIds.length})
                </Button>
              </>
            )}
            {canManageProject && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => handleElevationPasteFromClipboard('storefront')}
                  disabled={uploadingElevation || bulkDeletingElevations || elevationUploading === 'storefront'}
                >
                  {elevationUploading === 'storefront' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ClipboardPaste className="h-4 w-4 mr-2" />
                  )}
                  Paste
                </Button>
                <Button 
                  onClick={() => document.getElementById('sf-elevation-upload').click()}
                  disabled={uploadingElevation || bulkDeletingElevations}
                >
                  {uploadingElevation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {uploadingElevation ? 'Processing...' : 'Upload Drawing'}
                </Button>
                <input
                  id="sf-elevation-upload"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(e) => handleElevationUpload(e, 'storefront')}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>
        </div>
      )}

      {selectedStorefrontElevation && selectedStorefrontElevation.id ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setSelectedStorefrontElevation(null);
                setSelectedElevation(null);
                navigate(`/project/${projectId}/${activeCsiTab}/${activeSubTab}`, { replace: true });
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Elevations
            </Button>
            {canManageProject && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={(e) => confirmDeleteElevation(selectedElevation, e)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Elevation
              </Button>
            )}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>{selectedStorefrontElevation.elevationName || selectedStorefrontElevation.filename}</CardTitle>
              <CardDescription>
                {sfBulkMarkerMode 
                  ? 'Select a storefront schedule, then click on the image to add multiple markers. Press Done when finished.'
                  : 'Click on the image to mark a storefront schedule location'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {getElevationImageUrl(selectedStorefrontElevation) ? (
                <>
                  {/* Bulk Marker Controls + Zoom Controls */}
                  <div className="flex items-center gap-2 mb-3 justify-between">
                    {/* Left side - Bulk Marker Controls */}
                    <div className="flex items-center gap-2">
                      {!sfBulkMarkerMode ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSfBulkMarkerMode(true)}
                          disabled={storefrontPanelList.length === 0}
                          title="Add Multiple Markers"
                          data-testid="sf-elevation-add-multiple-markers-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Multiple Schedule Markers
                        </Button>
                      ) : (
                        <>
                          <Select
                            value={sfBulkMarkerSchedule}
                            onValueChange={setSfBulkMarkerSchedule}
                            disabled={savingSfBulkMarkers}
                          >
                            <SelectTrigger className="w-[260px]" data-testid="sf-bulk-marker-schedule-select">
                              <SelectValue placeholder="Select Schedule..." />
                            </SelectTrigger>
                            <SelectContent>
                              {storefrontPanelList.map((panel) => {
                                const sfType = storefrontTypes.find(t => t.id === panel.storefrontTypeId);
                                return (
                                  <SelectItem key={panel.id} value={panel.id} data-testid={`sf-bulk-marker-schedule-option-${panel.id}`}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                                        style={{ backgroundColor: sfType?.markerColor || '#39FF14' }}
                                      />
                                      {panel.label}
                                      <span className="text-xs text-slate-500">
                                        {[sfType?.name, panel.location].filter(Boolean).join(' • ')}
                                      </span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleSaveSfBulkMarkers}
                            disabled={pendingSfBulkMarkers.length === 0 || savingSfBulkMarkers}
                          >
                            {savingSfBulkMarkers ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>Done ({pendingSfBulkMarkers.length})</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelSfBulkMarkers}
                            disabled={savingSfBulkMarkers}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                    
                    {/* Right side - Zoom Controls */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleZoomOut}
                        disabled={elevationZoom <= 0.5}
                        title="Zoom Out"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium text-slate-600 min-w-[60px] text-center">
                        {Math.round(elevationZoom * 100)}%
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleZoomIn}
                        disabled={elevationZoom >= 3}
                        title="Zoom In"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetZoom}
                        disabled={isZoomAtDefault(elevationZoom)}
                        title="Reset zoom to 100%"
                        aria-label="Reset zoom to 100%"
                        data-testid="sf-elevation-reset-zoom-btn"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Image Container with Pan */}
                  <div 
                    className="relative overflow-auto border border-slate-200 rounded"
                    style={{ 
                      maxHeight: '70vh',
                      cursor: sfBulkMarkerMode && sfBulkMarkerSchedule 
                        ? 'crosshair' 
                        : elevationZoom > 1 
                          ? (isPanning ? 'grabbing' : 'grab') 
                          : 'default'
                    }}
                  >
                    <div 
                      className="relative inline-block"
                      style={{
                        transform: `scale(${elevationZoom}) translate(${elevationPan.x}px, ${elevationPan.y}px)`,
                        transformOrigin: 'top left',
                        transition: isPanning ? 'none' : 'transform 0.2s ease-out'
                      }}
                      onMouseMove={(e) => {
                        handlePanMove(e);
                        handleMarkerMouseMove(e);
                      }}
                      onMouseDown={handlePanStart}
                      onMouseUp={(e) => {
                        handlePanEnd();
                        handleMarkerMouseUp(e);
                      }}
                      onMouseLeave={(e) => {
                        handlePanEnd();
                        handleMarkerMouseUp(e);
                      }}
                    >
                      <img
                        src={getElevationImageUrl(selectedStorefrontElevation, 'large')}
                        alt={selectedStorefrontElevation.elevationName || "Elevation"}
                        className="w-full"
                        style={{ cursor: sfBulkMarkerMode && sfBulkMarkerSchedule ? 'crosshair' : 'crosshair' }}
                        onClick={(canManageProject) ? (e) => {
                          if (!isPanning) {
                            if (sfBulkMarkerMode && sfBulkMarkerSchedule) {
                              handleSfBulkMarkerClick(e);
                            } else if (!sfBulkMarkerMode) {
                              setMarkerContext('storefront');
                              handleImageClick(e);
                            }
                          }
                        } : null}
                        draggable={false}
                      />
                    
                    {/* Display Markers */}
                    {selectedStorefrontElevation?.markers && Array.isArray(selectedStorefrontElevation.markers) && selectedStorefrontElevation.markers
                      .filter((marker) => {
                        return storefrontTypes.some(sf => sf.id === marker.windowListId) || 
                               storefrontPanelList.some(p => p.id === marker.windowListId);
                      })
                      .map((marker) => {
                        // Markers reference a Storefront Schedule; legacy markers may still
                        // reference a Storefront Type, so fall back to it for colour only.
                        const sfSchedule = storefrontPanelList.find(p => p.id === marker.windowListId);
                        const sfType = sfSchedule
                          ? storefrontTypes.find(t => t.id === sfSchedule.storefrontTypeId)
                          : storefrontTypes.find(t => t.id === marker.windowListId);
                        const markerColor = sfType?.markerColor || '#39FF14';
                        
                        return (
                          <div
                            key={marker.id}
                            className="absolute z-10"
                            style={{
                              left: `${marker.x}%`,
                              top: `${marker.y}%`,
                              transform: 'translate(-50%, -50%)',
                              cursor: 'pointer',
                              padding: '8px',
                              zIndex: activeMarkerPopup === marker.id ? 50 : 10
                            }}
                            onMouseDown={(e) => {
                              if (canManageProject && !e.target.closest('.marker-popup')) {
                                handleMarkerMouseDown(e, marker);
                              }
                            }}
                          >
                            <div 
                              style={{
                                width: '12px',
                                height: '12px',
                                border: `2px solid ${markerColor}`,
                                backgroundColor: activeMarkerPopup === marker.id ? markerColor : `${markerColor}1A`,
                                boxShadow: `0 0 8px ${markerColor}99`,
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = markerColor}
                              onMouseLeave={(e) => {
                                if (activeMarkerPopup !== marker.id) {
                                  e.currentTarget.style.backgroundColor = `${markerColor}1A`;
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setActiveMarkerPopup(activeMarkerPopup === marker.id ? null : marker.id);
                              }}
                            />
                            {activeMarkerPopup === marker.id && (
                              <MarkerActionsPopup zoom={elevationZoom}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMarkerPopup(null);
                                    handleMarkerClick(e, marker);
                                  }}
                                  className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors flex items-center justify-center"
                                  title="Detayları Görüntüle"
                                  style={{ minWidth: '24px', minHeight: '24px' }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                
                                {canManageProject && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarkerPopup(null);
                                      handleDeleteMarker(marker.id);
                                    }}
                                    className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors flex items-center justify-center"
                                    title="Marker'ı Sil"
                                    style={{ minWidth: '24px', minHeight: '24px' }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMarkerPopup(null);
                                  }}
                                  className="p-1 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors flex items-center justify-center"
                                  title="Kapat"
                                  style={{ minWidth: '24px', minHeight: '24px' }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </MarkerActionsPopup>
                            )}
                          </div>
                        );
                      })}
                    
                    {/* Pending Bulk Markers */}
                    {pendingSfBulkMarkers.map((marker) => (
                      <div
                        key={marker.id}
                        className="absolute group z-20"
                        style={{
                          left: `${marker.x}%`,
                          top: `${marker.y}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <div 
                          style={{
                            width: '10px',
                            height: '10px',
                            border: `2px dashed ${marker.color}`,
                            backgroundColor: `${marker.color}33`,
                            boxShadow: `0 0 8px ${marker.color}99`
                          }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePendingSfMarker(marker.id);
                          }}
                          className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-slate-500">No images available. PDF conversion may have failed.</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <>
          {storefrontElevations.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-slate-600">No elevations uploaded yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {storefrontElevations.map((elevation) => (
                <Card 
                  key={elevation.id} 
                  className={`cursor-pointer hover:shadow-lg transition-shadow relative ${selectedStorefrontElevationIds.includes(elevation.id) ? 'ring-2 ring-blue-500' : ''}`} 
                  onClick={() => handleElevationClick(elevation, 'storefront')}
                >
                  {canManageProject && (
                    <>
                      <div 
                        className="absolute top-2 left-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedStorefrontElevationIds.includes(elevation.id)}
                          onCheckedChange={(checked) => toggleElevationSelection(elevation.id, 'storefront', { target: { checked } })}
                          className="h-5 w-5"
                        />
                      </div>
                      <button
                        onClick={(e) => confirmDeleteElevation(elevation, e)}
                        className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <CardContent className="p-4">
                    <div className="aspect-video bg-slate-100 rounded mb-3 flex items-center justify-center overflow-hidden">
                      {getElevationImageUrl(elevation) ? (
                        <img 
                          src={getElevationImageUrl(elevation, 'thumbnail')} 
                          alt="Elevation preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <p className="text-sm text-slate-500">PDF Preview</p>
                      )}
                    </div>
                    <p className="font-medium text-sm truncate">{elevation.elevationName || elevation.filename}</p>
                    {elevation.aiDetected && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">AI Detected</span>
                    )}
                    <p className="text-xs text-slate-500 mt-1">{new Date(elevation.uploadedAt).toLocaleDateString()}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      {elevation.markers?.length || 0} schedule(s) marked
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </TabsContent>
  );
};

export default SFElevationsSection;
